import type React from 'react';
import { useTranslation } from 'react-i18next';
import { DropZone } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    XLSX_ACCEPT,
    type DroppedItem,
    type FileRefusal,
} from '../../uploadIntake';
import { UploadRefusalAlert } from '../uploadNotices';

type UploadDropZoneProps = {
    /** The row limit, formatted for the hint. */
    limit: string;
    refusal: FileRefusal | null;
    onDrop: (event: { items: DroppedItem[] }) => void;
    onFilesPicked: (files: readonly File[]) => void;
};

// A dragged file often has no usable media type, so every drop is taken and
// the step's intake refuses what is not a workbook, with its own message.
const getDropOperation = () => 'copy' as const;

/**
 * The drop zone: drag a file in, or choose one through the hidden input
 * (`.xlsx` only, one file). A refused pick is explained below it, and the
 * zone stays for the next try.
 */
export const UploadDropZone: React.FC<UploadDropZoneProps> = ({
    limit,
    refusal,
    onDrop,
    onFilesPicked,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div className="flex flex-col gap-3">
            <DropZone
                className="w-full"
                {...getNodeTestIdProp('catalog-import-drop-zone')}
            >
                <DropZone.Area
                    className="min-h-72 w-full"
                    getDropOperation={getDropOperation}
                    onDrop={onDrop}
                    {...getNodeTestIdProp('catalog-import-drop-zone-area')}
                >
                    <DropZone.Icon />
                    <DropZone.Label>
                        {t('catalogImport.upload.drop.label')}
                    </DropZone.Label>
                    <DropZone.Description>
                        {t('catalogImport.upload.drop.hint', { limit })}
                    </DropZone.Description>
                    <DropZone.Trigger
                        {...getNodeTestIdProp(
                            'catalog-import-drop-zone-choose',
                        )}
                    >
                        {t('catalogImport.upload.drop.choose')}
                    </DropZone.Trigger>
                </DropZone.Area>
                <DropZone.Input
                    accept={XLSX_ACCEPT}
                    onSelect={(files) =>
                        onFilesPicked(files ? Array.from(files) : [])
                    }
                    {...getNodeTestIdProp('catalog-import-drop-zone-input')}
                />
            </DropZone>
            {refusal ? <UploadRefusalAlert refusal={refusal} /> : null}
        </div>
    );
};
